const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

async function fetchAPI(url: string, options: RequestInit = {}): Promise<any> {
  const token = localStorage.getItem('token');
  const contentType = options.method === 'PATCH' ? 'application/merge-patch+json' : 'application/ld+json';
  const headers = {
    'Content-Type': contentType,
    Accept: 'application/ld+json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };

  const response = await fetch(url, { ...options, headers });

  if (response.status === 204) {
    return null;
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData['hydra:description'] || errorData.message || `HTTP error! status: ${response.status}`);
  }

  return response.json();
}

export interface CourseRating {
  id: number;
  courseId: number;
  rating: number;
  comment?: string;
  createdAt: string;
  updatedAt: string;
}

export interface RatingSummary {
  averageRating: number | null;
  ratingCount: number;
}

export interface RatingsBulkResult {
  [courseId: number]: RatingSummary;
}

class RatingService {
  /**
   * Get rating summary (average + count) for a course
   */
  async getRatingSummary(courseId: number): Promise<RatingSummary> {
    return fetchAPI(`${API_BASE_URL}/api/courses/${courseId}/rating-summary`);
  }

  /**
   * Get rating summaries for multiple courses (for catalog)
   */
  async getRatingsBulk(courseIds: number[]): Promise<RatingsBulkResult> {
    if (courseIds.length === 0) {
      return {};
    }
    return fetchAPI(`${API_BASE_URL}/api/courses/ratings-bulk`, {
      method: 'POST',
      body: JSON.stringify({ courseIds }),
    });
  }

  /**
   * Get current user's rating for a course
   */
  async getMyRating(courseId: number): Promise<CourseRating | null> {
    return fetchAPI(`${API_BASE_URL}/api/courses/${courseId}/my-rating`);
  }

  /**
   * Submit a new rating for a course
   */
  async submitRating(courseId: number, rating: number, comment?: string): Promise<CourseRating> {
    const response = await fetchAPI(`${API_BASE_URL}/api/course_ratings`, {
      method: 'POST',
      body: JSON.stringify({
        course: `/api/courses/${courseId}`,
        rating,
        comment: comment || null,
      }),
    });

    return {
      id: response.id,
      courseId: courseId,
      rating: response.rating,
      comment: response.comment,
      createdAt: response.createdAt,
      updatedAt: response.updatedAt,
    };
  }

  /**
   * Update an existing rating
   */
  async updateRating(ratingId: number, rating: number, comment?: string): Promise<CourseRating> {
    const response = await fetchAPI(`${API_BASE_URL}/api/course_ratings/${ratingId}`, {
      method: 'PATCH',
      body: JSON.stringify({
        rating,
        comment: comment || null,
      }),
    });

    return {
      id: response.id,
      courseId: response.course?.id || 0,
      rating: response.rating,
      comment: response.comment,
      createdAt: response.createdAt,
      updatedAt: response.updatedAt,
    };
  }

  /**
   * Delete a rating
   */
  async deleteRating(ratingId: number): Promise<void> {
    await fetchAPI(`${API_BASE_URL}/api/course_ratings/${ratingId}`, {
      method: 'DELETE',
    });
  }

  /**
   * Get all ratings for a course (anonymous list)
   */
  async getCourseRatings(courseId: number): Promise<CourseRating[]> {
    const response = await fetchAPI(`${API_BASE_URL}/api/course_ratings?course.id=${courseId}`);
    return (response['hydra:member'] || response['member'] || []).map((r: any) => ({
      id: r.id,
      courseId: courseId,
      rating: r.rating,
      comment: r.comment,
      createdAt: r.createdAt,
      updatedAt: r.updatedAt,
    }));
  }
}

export const ratingService = new RatingService();
export default ratingService;
